import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import {
    mdiMap, mdiTruckDeliveryOutline, mdiAlertBox,
    mdiDomain, mdiMapMarkerPath,
    mdiAlarmPanelOutline, mdiDatabaseCheck,
    mdiLogoutVariant, mdiAccountChildCircle, mdiGeneratorPortable,
    mdiBusMultiple,
} from '@mdi/js';

import {
    List, ListItem, ListItemIcon, ListItemText,
    Divider, Grid, Typography, Box, Paper,
    styled, IconButton, Badge
} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { keyframes } from '@mui/system';
import { hasGensetVehiclesSelector } from '../../features/assets/assetsSlice';

// Styled components
const MenuContainer = styled(Paper)(({ theme }) => ({
    width: 240,
    height: '100vh',
    background: `linear-gradient(180deg, 
        ${theme.palette.background.paper} 0%, 
        ${theme.palette.grey[100]} 75%, 
        ${theme.palette.grey[200]} 100%)`,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: 0,
    overflowX: 'hidden',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    margin: '2px 4px',
    borderRadius: theme.shape.borderRadius,
    padding: '4px 8px',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const pulseAnimation = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
`;

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: '#ffd700',
        color: '#d32f2f',
        fontSize: '0.65rem',
        height: 20,
        padding: '0 6px',
        animation: `${pulseAnimation} 2s infinite ease-in-out`,
        textTransform: 'uppercase',
        fontWeight: 'bold',
        border: '1px solid #ffd700',
    },
}));

const MainMenu = ({ thisUser, handleDrawerClose, setadminOpen, onLogOut }) => {
    const {
        username,
        isAdmin,
        isSubAccount,
        advancedFeatures = [],
    } = thisUser;
    const hasGensetVehicles = useSelector(hasGensetVehiclesSelector);
    const enabledAdvancedFeatures = new Set(advancedFeatures);

    const menuItems = [
        {
            key: 'livetracking',
            title: 'Live Map',
            category: 'basicFeatures',
            icon: mdiMap,
            color: '#2196F3',
            path: '/secured/livetracking',
            addDivider: true
        },
        {
            key: 'tripcenter',
            title: 'Trips & Routes',
            category: 'advancedFeatures',
            icon: mdiTruckDeliveryOutline,
            color: '#9C27B0',
            path: '/secured/tripcenter',
            isNew: true
        },
        {
            key: 'myfleet',
            title: 'My Fleet',
            category: 'basicFeatures',
            icon: mdiBusMultiple,
            color: '#00BFA5',
            path: '/secured/myfleet'
        },
        {
            key: 'gensetdashboard',
            title: 'Genset',
            category: 'basicFeatures',
            icon: mdiGeneratorPortable,
            color: '#FF5722',
            path: '/secured/gensetdashboard',
            hideIf: !hasGensetVehicles,
            addDivider: true
        },
        {
            key: 'events',
            title: 'Events',
            category: 'basicFeatures',
            icon: mdiAlertBox,
            color: '#FFA726',
            path: '/secured/eventview',
            hideIf: isSubAccount
        },
        {
            key: 'tracks',
            title: 'Tracks',
            category: 'basicFeatures',
            icon: mdiMapMarkerPath,
            color: '#66BB6A',
            path: '/secured/trackview'
        },
        {
            key: 'reports',
            title: 'Reports',
            category: 'basicFeatures',
            icon: mdiDatabaseCheck,
            color: '#5C6BC0',
            path: '/secured/reportview',
            addDivider: true
        },
        {
            key: 'places',
            title: 'Places',
            category: 'basicFeatures',
            icon: mdiDomain,
            color: '#26A69A',
            path: '/secured/placesview'
        },
        {
            key: 'subaccount',
            title: 'Subaccount',
            category: 'basicFeatures',
            icon: mdiAccountChildCircle,
            color: '#7E57C2',
            path: '/secured/subaccountview',
            hideIf: isSubAccount,
            addDivider: true
        },
        {
            key: 'cpanel',
            title: 'cPanel',
            category: 'basicFeatures',
            icon: mdiAlarmPanelOutline,
            color: '#EC407A',
            isAdmin: true,
            onClick: () => setadminOpen(true)
        },
        {
            key: 'logout',
            title: 'Logout',
            category: 'basicFeatures',
            icon: mdiLogoutVariant,
            color: '#EF5350',
            onClick: () => onLogOut(),
            textColor: 'Red'
        }
    ];

    return (
        <MenuContainer elevation={2}>
            <Box
                sx={{
                    width: '100%',
                    height: 48,
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: 'white',
                    }}
                >
                    App Menu
                </Typography>
                <IconButton
                    onClick={handleDrawerClose}
                    sx={{
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                    }}
                    size="small"
                >
                    <ChevronLeftIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    height: 'calc(100% - 48px)',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 1,
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <List
                        sx={{
                            width: '100%',
                            '& .MuiListItemIcon-root': {
                                minWidth: 40,
                            },
                        }}
                    >
                        {menuItems.map((item) => {
                            const isAdvancedFeatureDisabled = (
                                item.category === 'advancedFeatures'
                                && !enabledAdvancedFeatures.has(item.key)
                            );

                            if (item.hideIf || (item.isAdmin && !isAdmin) || isAdvancedFeatureDisabled) {
                                return null;
                            }
                            return (
                                <React.Fragment key={item.key}>
                                    <StyledListItem
                                        button
                                        component={item.path ? Link : 'div'}
                                        to={item.path}
                                        onClick={item.onClick || handleDrawerClose}
                                    >
                                        <ListItemIcon>
                                            <Icon
                                                path={item.icon}
                                                size={0.9}
                                                color={item.color}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                item.isNew ? (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}>
                                                        {item.title}
                                                        <StyledBadge
                                                            badgeContent="NEW"
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    position: 'relative',
                                                                    transform: 'none',
                                                                    marginLeft: 1,
                                                                    fontSize: '0.6rem',
                                                                    height: 18,
                                                                    minWidth: '32px',
                                                                    padding: '0 4px',
                                                                    marginRight: 1
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                ) : item.title
                                            }
                                            primaryTypographyProps={{
                                                variant: 'subtitle1',
                                                color: item.textColor,
                                                sx: { fontSize: '0.85rem' }
                                            }}
                                        />
                                    </StyledListItem>
                                    {item.addDivider && (
                                        <Divider
                                            sx={{
                                                my: 1,
                                                mx: 2,
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Box>

                <Box sx={{ px: 2, mb: 8 }}>
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'royalblue',
                                    fontWeight: 600,
                                    mb: 0.5
                                }}
                            >
                                Account
                            </Typography>
                            <Typography variant="subtitle1">
                                {username}
                            </Typography>
                        </Grid>

                        <Grid item>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'royalblue',
                                    fontWeight: 600,
                                    mb: 0.5
                                }}
                            >
                                Support
                            </Typography>
                            <Typography variant="subtitle1">
                                +91 9372 800 056
                            </Typography>
                        </Grid>

                        <Grid item>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'royalblue',
                                    fontWeight: 600,
                                    mb: 0.5
                                }}
                            >
                                Sales
                            </Typography>
                            <Typography variant="subtitle1">
                                +91 9867 987 987
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        width: 240,
                        borderTop: 1,
                        borderColor: 'divider',
                        p: 1.5,
                        //backgroundColor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <img
                        src="/logo.png"
                        alt="Roadmatics"
                        style={{ width: 28, height: 28 }}
                    />
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(45deg, #000000, #444444)',
                            backgroundClip: 'text',
                            color: 'transparent',
                        }}
                    >
                        Roadmatics
                    </Typography>
                </Box>
            </Box>
        </MenuContainer>
    );
};

export default MainMenu;
